import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Plus, Pencil, Trash2, Eye, History, Save } from 'lucide-react';
import { useDocsI18n } from '../../i18n';
import { cn } from '../../lib/utils';

export function AdminChangelog(props) {
    const { t } = useDocsI18n();
  const { changelogDialogOpen, changelogEntries, changelogForm, docs, editingChangelog, fetchData, navigate, setChangelogDialogOpen, setChangelogForm, setDeleteDialogOpen, setEditingChangelog, setItemToDelete } = props;
  return (
    <>
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h1 className="text-3xl font-mono font-bold">Changelog</h1>
                                <div className="flex gap-2">
                                    <Button variant="outline" onClick={() => navigate('/changelog')}>
                                        <Eye className="w-4 h-4 mr-2" />
                                        Öffentliche Seite
                                    </Button>
                                    <Button onClick={() => { setEditingChangelog(null); setChangelogForm({ title: '', description: '', type: 'update', doc_id: '', project_id: '', is_visible: true }); setChangelogDialogOpen(true); }}>
                                        <Plus className="w-4 h-4 mr-2" />
                                        Neuer Eintrag
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-3">
                                {changelogEntries.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-8">{t('noChangelog')}</p>
                                ) : (
                                    changelogEntries.map(entry => (
                                        <div key={entry.id} className={cn(
                                            "p-4 rounded-lg border border-border bg-card/50 group",
                                            !entry.is_visible && "opacity-60"
                                        )}>
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <Badge variant="outline" className={cn(
                                                            "text-xs",
                                                            entry.type === 'new' && "bg-green-500/20 text-green-500 border-green-500/30",
                                                            entry.type === 'update' && "bg-blue-500/20 text-blue-500 border-blue-500/30",
                                                            entry.type === 'fix' && "bg-orange-500/20 text-orange-500 border-orange-500/30",
                                                            entry.type === 'remove' && "bg-red-500/20 text-red-500 border-red-500/30"
                                                        )}>
                                                            {entry.type === 'new' ? 'Neu' : entry.type === 'update' ? 'Update' : entry.type === 'fix' ? 'Fix' : 'Entfernt'}
                                                        </Badge>
                                                        {entry.is_auto && <Badge variant="secondary" className="text-xs">Auto</Badge>}
                                                        {!entry.is_visible && <Badge variant="outline" className="text-xs">Versteckt</Badge>}
                                                        <span className="text-xs text-muted-foreground">
                                                            {new Date(entry.created_at).toLocaleDateString('de-DE')}
                                                        </span>
                                                    </div>
                                                    <h3 className="font-mono font-medium">{entry.title}</h3>
                                                    <p className="text-sm text-muted-foreground mt-1">{entry.description}</p>
                                                    {entry.doc_title && (
                                                        <span className="text-xs text-primary mt-1 inline-block">→ {entry.doc_title}</span>
                                                    )}
                                                </div>
                                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button variant="ghost" size="icon" onClick={() => {
                                                        setEditingChangelog(entry);
                                                        setChangelogForm({
                                                            title: entry.title,
                                                            description: entry.description,
                                                            type: entry.type,
                                                            doc_id: entry.doc_id || '',
                                                            project_id: entry.project_id || '',
                                                            is_visible: entry.is_visible
                                                        });
                                                        setChangelogDialogOpen(true);
                                                    }}>
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => {
                                                        setItemToDelete({ type: 'changelog', id: entry.id, name: entry.title });
                                                        setDeleteDialogOpen(true);
                                                    }}>
                                                        <Trash2 className="w-4 h-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </motion.div>

            {/* Changelog Dialog */}
            <Dialog open={changelogDialogOpen} onOpenChange={setChangelogDialogOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-mono">
                            {editingChangelog ? 'Changelog bearbeiten' : 'Neuer Changelog-Eintrag'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                            if (editingChangelog) {
                                await axios.put(`${API}/changelog/${editingChangelog.id}?title=${encodeURIComponent(changelogForm.title)}&description=${encodeURIComponent(changelogForm.description)}&type=${changelogForm.type}&is_visible=${changelogForm.is_visible}`);
                                toast.success('Changelog-Eintrag aktualisiert');
                            } else {
                                await axios.post(`${API}/changelog`, changelogForm);
                                toast.success('Changelog-Eintrag erstellt');
                            }
                            setChangelogDialogOpen(false);
                            fetchData();
                        } catch (error) {
                            toast.error(error.response?.data?.detail || t('saveFailed'));
                        }
                    }} className="space-y-4">
                        <div>
                            <Label>{t('title')}</Label>
                            <Input
                                value={changelogForm.title}
                                onChange={e => setChangelogForm({ ...changelogForm, title: e.target.value })}
                                placeholder={t('changelogTitlePh')}
                                required
                            />
                        </div>
                        <div>
                            <Label>{t('description')}</Label>
                            <Textarea
                                value={changelogForm.description}
                                onChange={e => setChangelogForm({ ...changelogForm, description: e.target.value })}
                                placeholder={t('changelogDescPh')}
                                rows={3}
                                required
                            />
                        </div>
                        <div>
                            <Label>{t('type')}</Label>
                            <Select value={changelogForm.type} onValueChange={v => setChangelogForm({ ...changelogForm, type: v })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="new">Neu</SelectItem>
                                    <SelectItem value="update">Update</SelectItem>
                                    <SelectItem value="fix">Fix</SelectItem>
                                    <SelectItem value="remove">Entfernt</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{t('linkedDoc')}</Label>
                            <Select value={changelogForm.doc_id || "none"} onValueChange={v => setChangelogForm({ ...changelogForm, doc_id: v === "none" ? "" : v })}>
                                <SelectTrigger>
                                    <SelectValue placeholder={t('none')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">{t('none')}</SelectItem>
                                    {docs.filter(d => d.is_published).map(d => (
                                        <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                id="changelog-visible"
                                checked={changelogForm.is_visible}
                                onCheckedChange={v => setChangelogForm({ ...changelogForm, is_visible: v })}
                            />
                            <Label htmlFor="changelog-visible">{t('publicVisible')}</Label>
                        </div>
                        <DialogFooter>
                            <Button type="submit">
                                <Save className="w-4 h-4 mr-2" />
                                Speichern
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
    </>
  );
}
